import { useState, useRef } from 'react'
import Axios from 'axios'
import getConfig from 'next/config'
import ReCAPTCHA from 'react-google-recaptcha'
import { useTranslation } from 'next-i18next'
import { makeStyles, Container } from '@material-ui/core'
import T, { specularHost } from '../home-new/tokens'
import Reveal from '../home-new/Reveal'

const { publicRuntimeConfig } = getConfig()

// Section 8 — "แบบฟอร์มติดต่อทีมขาย" (id="contact-form", the hero CTA anchor).
// Mirrors the old "Apply for SHIPPOP PRO" form (pages/pro.js) and submits to the
// SAME proven backend, /api/pro/apply -> /register/shippop-pro/, which accepts
// { name, email, phone, line, time, shippingPerMonth, category, url, shop }.
// The optional "API KEY test" checkbox adds register_channel:'www' to that
// SAME request — the backend mints a Sandbox API key inline and the result
// (api_key / api_key_error) comes back in the same response. Everything goes
// through one endpoint, one reCAPTCHA execution, one request.
//
// State keys map to the backend as: callTime->time (timeRangeId 1|2),
// volume->shippingPerMonth (a plain number), shopType->category (one of the
// fixed store categories), shopLink->url, franchise->shop (interested?).

// Contact-time windows the backend understands (timeRangeId). Same two IDs the
// old SHIPPOP PRO form sends — the backend only recognises these.
const TIME_CHOICES = [
  [1, '9:00 - 12:00'],
  [2, '13:00 - 17:00'],
]

// Store categories — identical list to the old SHIPPOP PRO form so the backend
// (/register/shippop-pro/) receives a value it recognises. Kept in Thai for
// both languages, matching the old form's behaviour.
const CATEGORY_CHOICES = [
  'เสื้อผ้า แฟชั่น',
  'เครื่องประดับ',
  'สุขภาพ',
  'ความงาม',
  'แม่และเด็ก',
  'ของเล่นและเกมส์',
  'หนังและบันเทิง',
  'ไลฟ์สไตล์',
  'หนังสือและของสะสม',
  'กล้องและอุปกรณ์เสริม',
  'เครื่องใช้ไฟฟ้า',
  'โทรศัพท์และคอมพิวเตอร์',
  'เฟอร์นิเจอร์ของตกแต่ง',
  'ยานยนต์และอุปกรณ์',
  'สินค้าและอุปกรณ์การเกษตร',
  'อุปกรณ์การแพทย์',
  'ร้านยาและเวชภัณฑ์',
  'วัสดุและอุปกรณ์ก่อสร้าง',
  'อาหารและเครื่องดื่ม',
  'อื่นๆ',
]

const initial = {
  nameSurname: '',
  email: '',
  phone: '',
  lineId: '',
  callTime: '',
  volume: '',
  shopType: '',
  shopLink: '',
  franchise: false,
  apiInterest: false,
}

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '70px 0',
    background: T.color.canvas,
    [theme.breakpoints.down('sm')]: {
      padding: '44px 0',
    },
  },
  heading: {
    fontFamily: T.font.heading,
    fontSize: 32,
    fontWeight: 700,
    color: T.color.ink,
    textAlign: 'center',
    margin: '0 0 12px',
    [theme.breakpoints.down('sm')]: { fontSize: 22 },
  },
  lead: {
    fontFamily: T.font.heading,
    fontSize: 20,
    fontWeight: 600,
    color: T.color.brand,
    textAlign: 'center',
    margin: '0 0 10px',
    [theme.breakpoints.down('sm')]: { fontSize: 16 },
  },
  sub: {
    fontFamily: T.font.body,
    fontSize: 16,
    color: T.color.muted,
    textAlign: 'center',
    margin: '0 auto 34px',
    maxWidth: 640,
    [theme.breakpoints.down('sm')]: { fontSize: 14, marginBottom: 24 },
  },
  card: {
    background: T.color.surface,
    borderRadius: 24,
    boxShadow: T.shadow.lg,
    padding: '40px 44px 36px',
    maxWidth: 940,
    margin: '0 auto',
    [theme.breakpoints.down('sm')]: {
      padding: '26px 20px 24px',
    },
  },
  fieldGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px 22px',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      gap: '16px',
    },
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
  },
  // The reference form is placeholder-only (no visible labels above each
  // field), so labels are kept in the DOM for screen readers but visually
  // hidden. The field's own placeholder carries the name for sighted users.
  label: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  input: {
    fontFamily: T.font.body,
    fontSize: 15,
    color: T.color.ink,
    background: T.color.surface,
    border: `1.5px solid ${T.color.line}`,
    borderRadius: T.radius.md,
    padding: '0 16px',
    height: 52,
    width: '100%',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    '&:focus': {
      outline: 'none',
      borderColor: T.color.brand,
      boxShadow: '0 0 0 3px rgba(0,158,255,0.15)',
    },
    '&::placeholder': { color: T.color.faint },
  },
  select: {
    appearance: 'none',
    backgroundImage:
      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 16 16' fill='none'%3E%3Cpath d='M4 6l4 4 4-4' stroke='%23666666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 16px center',
    paddingRight: 40,
    cursor: 'pointer',
  },
  inputError: {
    borderColor: '#E53935',
    '&:focus': { boxShadow: '0 0 0 3px rgba(229,57,53,0.15)' },
  },
  errorText: {
    fontFamily: T.font.body,
    fontSize: 13,
    color: '#E53935',
    margin: '6px 2px 0',
    minHeight: 16,
  },
  checkRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    margin: '20px 2px 0',
    fontFamily: T.font.body,
    fontSize: 14,
    color: T.color.inkSoft,
    lineHeight: 1.5,
  },
  checkbox: {
    width: 20,
    height: 20,
    flexShrink: 0,
    marginTop: 1,
    accentColor: T.color.brand,
    cursor: 'pointer',
  },
  checkLabel: { cursor: 'pointer' },
  inlineLink: {
    color: T.color.brand,
    textDecoration: 'none',
    fontWeight: 500,
    '&:hover': { textDecoration: 'underline' },
  },
  submitRow: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 30,
  },
  submit: {
    ...specularHost,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 52,
    padding: '0 46px',
    borderRadius: T.radius.pill,
    background: T.color.ink,
    color: T.color.onBrand,
    fontFamily: T.font.heading,
    fontSize: 17,
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    boxShadow: T.shadow.md,
    '&:hover': { transform: 'translateY(-2px)', color: T.color.onBrand },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
      transform: 'none',
    },
  },
  statusMsg: {
    fontFamily: T.font.body,
    fontSize: 15,
    textAlign: 'center',
    margin: '18px auto 0',
    padding: '12px 20px',
    borderRadius: T.radius.md,
    maxWidth: 560,
  },
  statusOk: {
    background: 'rgba(22,163,74,0.1)',
    color: T.color.success,
  },
  statusErr: {
    background: 'rgba(229,57,53,0.1)',
    color: '#E53935',
  },
}))

const ContactFormBiz = () => {
  const classes = useStyles()
  const { t } = useTranslation('business')
  const [form, setForm] = useState(initial)
  const [errors, setErrors] = useState({})
  const [status, setStatus] = useState(null) // null | 'loading' | 'success' | 'error'
  const [apiNote, setApiNote] = useState(null) // null | 'sent' | 'already' | 'failed'
  const [submittedEmail, setSubmittedEmail] = useState('')
  const recaptchaRef = useRef(null)

  const validators = {
    nameSurname: (v) => (v.trim() ? '' : t('form.errors.nameRequired')),
    phone: (v) => {
      if (!v.trim()) return t('form.errors.phoneRequired')
      if (!/^0\d{8,9}$/.test(v.replace(/[-\s]/g, ''))) return t('form.errors.phoneInvalid')
      return ''
    },
    // Email is required — the old SHIPPOP PRO backend requires it and the
    // API-KEY test needs an address to send to.
    email: (v) => {
      const val = v.trim()
      if (!val) return t('form.errors.emailRequired')
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) return t('form.errors.emailInvalid')
      return ''
    },
    // Required by the SHIPPOP PRO backend (time, shippingPerMonth, category).
    callTime: (v) => (v ? '' : t('form.errors.timeRequired')),
    volume: (v) => (String(v).trim() ? '' : t('form.errors.volumeRequired')),
    shopType: (v) => (v ? '' : t('form.errors.shopTypeRequired')),
    // Optional fields, but validate the FORMAT when filled — same rules the old
    // SHIPPOP PRO form used. Without this, a malformed value like "11" reaches
    // the backend and comes back as 400 "Invalid Data".
    lineId: (v) => {
      const val = v.trim()
      if (!val) return ''
      return /^@?\w+$/.test(val) ? '' : t('form.errors.lineInvalid')
    },
    shopLink: (v) => {
      const val = v.trim()
      if (!val) return ''
      return /[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)?/.test(
        val
      )
        ? ''
        : t('form.errors.urlInvalid')
    },
  }

  const setField = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }))
    if (errors[name]) setErrors((e) => ({ ...e, [name]: '' }))
  }

  const runValidate = (name, value) => {
    if (!validators[name]) return ''
    return validators[name](value)
  }

  const handleBlur = (name) => {
    const msg = runValidate(name, form[name])
    setErrors((e) => ({ ...e, [name]: msg }))
  }

  const validateAll = () => {
    const next = {}
    Object.keys(validators).forEach((k) => {
      const msg = validators[k](form[k])
      if (msg) next[k] = msg
    })
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (status === 'loading') return
    if (!validateAll()) return
    setStatus('loading')
    setApiNote(null)
    try {
      // Bot gate — mirrors the old SHIPPOP PRO form: pass reCAPTCHA before saving.
      const token = await recaptchaRef.current.executeAsync()
      const {
        data: { success: captchaOk },
      } = await Axios.post('/api/recaptcha/validate', { recaptchaToken: token })
      recaptchaRef.current.reset()
      if (!captchaOk) {
        setStatus('error')
        return
      }
      // Save the lead through the SAME proven backend the old SHIPPOP PRO form
      // uses (/api/pro/apply -> /register/shippop-pro/). No appointment date.
      const res = await Axios.post('/api/pro/apply', {
        name: form.nameSurname.trim(),
        email: form.email.trim(),
        phone: form.phone.replace(/[-\s]/g, ''),
        line: form.lineId.trim(),
        time: form.callTime,
        shippingPerMonth: form.volume,
        category: form.shopType,
        url: form.shopLink.trim(),
        shop: form.franchise ? 'interested' : 'not-interested',
        register_channel: form.apiInterest ? 'www' : '',
      })
      const leadOk = res.data && res.data.status === 'success'
      if (!leadOk) {
        setStatus('error')
        return
      }
      // Only extra behaviour vs the old form: optionally request a test API
      // key. The backend mints it as part of the SAME /register/shippop-pro/
      // call (when register_channel:'www' was sent above) and returns the
      // outcome inline — no second request needed.
      if (form.apiInterest) {
        const keyData = res.data.response?.data
        if (keyData?.api_key) {
          // A key was minted and emailed.
          setApiNote('sent')
        } else if (keyData?.api_key_error) {
          // Registration succeeded but key-generation sub-service failed —
          // surface it as a real failure so we don't tell the user to check
          // an email that never got a key.
          setApiNote('failed')
        } else {
          // No key and no error -> the email genuinely already has one.
          setApiNote('already')
        }
      }
      setSubmittedEmail(form.email.trim())
      setStatus('success')
      setForm(initial)
      setErrors({})
    } catch (err) {
      setStatus('error')
    }
  }

  const inputCls = (name) =>
    `${classes.input} ${errors[name] ? classes.inputError : ''}`

  return (
    <section className={classes.root} id="contact-form">
      <Container maxWidth={false} style={{ maxWidth: T.container }}>
        <Reveal>
          <h2 className={classes.heading}>{t('form.heading')}</h2>
          <p className={classes.lead}>{t('form.lead')}</p>
          <p className={classes.sub}>
            {t('form.sub')}
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <form className={classes.card} onSubmit={handleSubmit} noValidate>
            <div className={classes.fieldGrid}>
              <div className={classes.field}>
                <label className={classes.label} htmlFor="f-name">
                  {t('form.fields.name')} <b>*</b>
                </label>
                <input
                  id="f-name"
                  className={inputCls('nameSurname')}
                  placeholder={t('form.fields.name')}
                  value={form.nameSurname}
                  onChange={(e) => setField('nameSurname', e.target.value)}
                  onBlur={() => handleBlur('nameSurname')}
                  aria-invalid={!!errors.nameSurname}
                  aria-describedby="err-name"
                />
                <span id="err-name" className={classes.errorText} aria-live="polite">
                  {errors.nameSurname}
                </span>
              </div>

              <div className={classes.field}>
                <label className={classes.label} htmlFor="f-email">{t('form.fields.email')}</label>
                <input
                  id="f-email"
                  type="email"
                  className={inputCls('email')}
                  placeholder={t('form.fields.email')}
                  value={form.email}
                  onChange={(e) => setField('email', e.target.value)}
                  onBlur={() => handleBlur('email')}
                  aria-invalid={!!errors.email}
                  aria-describedby="err-email"
                />
                <span id="err-email" className={classes.errorText} aria-live="polite">
                  {errors.email}
                </span>
              </div>

              <div className={classes.field}>
                <label className={classes.label} htmlFor="f-phone">
                  {t('form.fields.phone')} <b>*</b>
                </label>
                <input
                  id="f-phone"
                  type="tel"
                  className={inputCls('phone')}
                  placeholder={t('form.fields.phone')}
                  value={form.phone}
                  onChange={(e) => setField('phone', e.target.value)}
                  onBlur={() => handleBlur('phone')}
                  aria-invalid={!!errors.phone}
                  aria-describedby="err-phone"
                />
                <span id="err-phone" className={classes.errorText} aria-live="polite">
                  {errors.phone}
                </span>
              </div>

              <div className={classes.field}>
                <label className={classes.label} htmlFor="f-line">{t('form.fields.lineId')}</label>
                <input
                  id="f-line"
                  className={inputCls('lineId')}
                  placeholder={t('form.fields.lineId')}
                  value={form.lineId}
                  onChange={(e) => setField('lineId', e.target.value)}
                  onBlur={() => handleBlur('lineId')}
                  aria-invalid={!!errors.lineId}
                />
                <span className={classes.errorText} aria-live="polite">
                  {errors.lineId}
                </span>
              </div>

              <div className={classes.field}>
                <label className={classes.label} htmlFor="f-time">
                  {t('form.fields.callTime')} <b>*</b>
                </label>
                <select
                  id="f-time"
                  className={`${inputCls('callTime')} ${classes.select}`}
                  value={form.callTime}
                  onChange={(e) => setField('callTime', e.target.value)}
                  onBlur={() => handleBlur('callTime')}
                  style={{ color: form.callTime ? T.color.ink : T.color.faint }}
                >
                  <option value="">{t('form.fields.callTime')}</option>
                  {TIME_CHOICES.map(([id, label]) => (
                    <option key={id} value={id}>{label}</option>
                  ))}
                </select>
                <span className={classes.errorText} aria-live="polite">
                  {errors.callTime}
                </span>
              </div>

              <div className={classes.field}>
                <label className={classes.label} htmlFor="f-volume">
                  {t('form.fields.volume')} <b>*</b>
                </label>
                <input
                  id="f-volume"
                  type="number"
                  min="1"
                  className={inputCls('volume')}
                  placeholder={t('form.fields.volume')}
                  value={form.volume}
                  onChange={(e) => setField('volume', e.target.value)}
                  onBlur={() => handleBlur('volume')}
                  aria-invalid={!!errors.volume}
                />
                <span className={classes.errorText} aria-live="polite">
                  {errors.volume}
                </span>
              </div>

              <div className={classes.field}>
                <label className={classes.label} htmlFor="f-shop">
                  {t('form.fields.shopType')} <b>*</b>
                </label>
                <select
                  id="f-shop"
                  className={`${inputCls('shopType')} ${classes.select}`}
                  value={form.shopType}
                  onChange={(e) => setField('shopType', e.target.value)}
                  onBlur={() => handleBlur('shopType')}
                  style={{ color: form.shopType ? T.color.ink : T.color.faint }}
                >
                  <option value="">{t('form.fields.shopType')}</option>
                  {CATEGORY_CHOICES.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
                <span className={classes.errorText} aria-live="polite">
                  {errors.shopType}
                </span>
              </div>

              <div className={classes.field}>
                <label className={classes.label} htmlFor="f-link">
                  {t('form.fields.shopLink')}
                </label>
                <input
                  id="f-link"
                  className={inputCls('shopLink')}
                  placeholder={t('form.fields.shopLink')}
                  value={form.shopLink}
                  onChange={(e) => setField('shopLink', e.target.value)}
                  onBlur={() => handleBlur('shopLink')}
                  aria-invalid={!!errors.shopLink}
                />
                <span className={classes.errorText} aria-live="polite">
                  {errors.shopLink}
                </span>
              </div>
            </div>

            <label className={classes.checkRow} htmlFor="f-franchise">
              <input
                id="f-franchise"
                type="checkbox"
                className={classes.checkbox}
                checked={form.franchise}
                onChange={(e) => setField('franchise', e.target.checked)}
              />
              <span className={classes.checkLabel}>
                {t('form.franchise.text')}{' '}
                <a
                  href="https://www.shippop.shop/"
                  className={classes.inlineLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('form.franchise.readMore')}
                </a>
              </span>
            </label>

            <label className={classes.checkRow} htmlFor="f-api-interest">
              <input
                id="f-api-interest"
                type="checkbox"
                className={classes.checkbox}
                checked={form.apiInterest}
                onChange={(e) => setField('apiInterest', e.target.checked)}
              />
              <span className={classes.checkLabel}>
                {t('form.fields.apiInterest')}
              </span>
            </label>

            <ReCAPTCHA
              sitekey={publicRuntimeConfig.recaptcha2Key}
              ref={recaptchaRef}
              size="invisible"
              badge="bottomleft"
            />

            <div className={classes.submitRow}>
              <button
                type="submit"
                className={classes.submit}
                disabled={status === 'loading'}
              >
                {status === 'loading' ? t('form.submit.loading') : t('form.submit.default')}
              </button>
            </div>

            {status === 'success' && (
              <p className={`${classes.statusMsg} ${classes.statusOk}`} role="status">
                {t('form.status.success')}
              </p>
            )}
            {status === 'success' && apiNote === 'sent' && (
              <p className={`${classes.statusMsg} ${classes.statusOk}`} role="status">
                {t('form.status.apiKeySent', { email: submittedEmail })}
              </p>
            )}
            {status === 'success' && apiNote === 'already' && (
              <p className={`${classes.statusMsg} ${classes.statusOk}`} role="status">
                {t('form.status.apiKeyAlready', { email: submittedEmail })}
              </p>
            )}
            {status === 'success' && apiNote === 'failed' && (
              <p className={`${classes.statusMsg} ${classes.statusErr}`} role="status">
                {t('form.status.apiKeyFailed')}
              </p>
            )}
            {status === 'error' && (
              <p className={`${classes.statusMsg} ${classes.statusErr}`} role="alert">
                {t('form.status.error')}
              </p>
            )}
          </form>
        </Reveal>
      </Container>
    </section>
  )
}

export default ContactFormBiz
